import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
} from 'firebase/firestore'
import { db } from './firebaseConfig'
// User interface
export interface FirestoreUser {
  name: string
  email: string
  role?: string
  roles?: string[]
  activeRole?: 'buyer' | 'seller'
}
// Create a new user document
export const createUser = async (userData: FirestoreUser, userId: string) => {
  try {
    const userRef = doc(db, 'users', userId)
    await setDoc(userRef, {
      ...userData,
      createdAt: new Date(),
    })
    return userId
  } catch (error) {
    console.error('Error creating user:', error)
    throw error
  }
}
// Get user by ID
export const getUserById = async (userId: string) => {
  try {
    const userRef = doc(db, 'users', userId)
    const userSnapshot = await getDoc(userRef)
    if (userSnapshot.exists()) {
      return {
        id: userSnapshot.id,
        ...userSnapshot.data(),
      } as FirestoreUser
    }
    return null
  } catch (error) {
    console.error('Error fetching user:', error)
    throw error
  }
}
// Update user
export const updateUser = async (
  userId: string,
  updates: Partial<FirestoreUser>,
) => {
  try {
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, updates)
    return true
  } catch (error) {
    console.error('Error updating user:', error)
    throw error
  }
}
// Delete all products created by a user
export const deleteUserProducts = async (userId: string) => {
  const productsRef = collection(db, 'products')
  const q = query(productsRef, where('sellerId', '==', userId))
  const querySnapshot = await getDocs(q)

  if (querySnapshot.empty) {
    return 0
  }

  const results = await Promise.allSettled(
    querySnapshot.docs.map((docSnap) => {
      return deleteDoc(doc(db, 'products', docSnap.id))
    }),
  )

  let succeeded = 0
  let failed = 0
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded++
    } else {
      failed++
      console.error(
        `Failed to delete product ${querySnapshot.docs[index].id}:`,
        result.reason,
      )
    }
  })

  if (failed > 0) {
    throw new Error(
      `Failed to delete ${failed} of ${querySnapshot.size} products`,
    )
  }
  return succeeded
}

// Delete user document from Firestore
export const deleteFirestoreUserDoc = async (userId: string) => {
  const userRef = doc(db, 'users', userId)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    return false
  }

  await deleteDoc(userRef)

  // Verify deletion
  const verifySnap = await getDoc(userRef)
  if (verifySnap.exists()) {
    console.error('VERIFICATION FAILED: User doc still exists after deletion')
    throw new Error('User document deletion could not be verified')
  }

  return true
}

// Keep the old export name for backward compatibility
export const deleteUser = deleteFirestoreUserDoc

// Full account cleanup: delete products, user doc, then return
// Auth deletion should be handled separately AFTER this succeeds
export const deleteUserAccountData = async (userId: string) => {
  // Step 1: Delete all user's products
  try {
    await deleteUserProducts(userId)
  } catch (productError) {
    console.error('Product deletion error:', productError)
    throw productError
  }

  // Step 2: Delete user document
  try {
    await deleteFirestoreUserDoc(userId)
  } catch (userDocError) {
    console.error('User doc deletion error:', userDocError)
    throw userDocError
  }

  return { success: true }
}
// Get notifications by user
export const getNotificationsByUser = async (userId: string) => {
  try {
    const notificationsRef = collection(db, 'notifications')
    const q = query(notificationsRef, where('userId', '==', userId))
    const querySnapshot = await getDocs(q)
    const notifications: any[] = []
    querySnapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() })
    })
    return notifications
  } catch (error) {
    console.error('Error fetching user notifications:', error)
    throw error
  }
}
// Mark notification as read
export const markNotificationAsRead = async (notificationId: string) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId)
    await updateDoc(notificationRef, { read: true })
    return true
  } catch (error) {
    console.error('Error marking notification as read:', error)
    throw error
  }
}
